import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarTopbar from "../../components/layouts/layout";
import 'font-awesome/css/font-awesome.min.css';
import "./Dashboard.css";
import { RiAiGenerate } from "react-icons/ri";
import { GrDocumentVideo, GrDocumentPpt, GrDocumentImage, GrDocumentSound } from "react-icons/gr";
import { VscGraph } from "react-icons/vsc";
import { HiOutlineClipboardDocument, HiOutlineDocumentChartBar, HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Dashboard = () => {
  const navigate = useNavigate();
  const [publications, setPublications] = useState([]);

  const [abstractVideosCount, setAbstractVideosCount] = useState(0);
  const [presentationsCount, setPresentationsCount] = useState(0);
  const [conferencePosterCount, setConferencePosterCount] = useState(0);
  const [audioSummariesCount, setAudioSummariesCount] = useState(0);

  const [randomClinicalTrialCount, setRandomClinicalTrialCount] = useState(0);
  const [clinicalTrialCount, setClinicalTrialCount] = useState(0);
  const [systematicReviewCount, setSystematicReviewCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  // Monthly analytics state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear] = useState(new Date().getFullYear());
  const [weeklyData, setWeeklyData] = useState([]);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Get available months (only months that have passed or are current)
  const getAvailableMonths = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const months = [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' },
    ];
    return months.filter(m => m.value <= currentMonth);
  };

  // Get user and organization data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");

  // Check if user is "demo" - if so, no restrictions apply
  const isDemoUser = userData.Username === "demo";


  const goToUpload = (targetPage) => {
    navigate("/uploadfile", { state: { target: targetPage } });
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    // Fetch asset counts from credits_usage endpoint
    const fetchAssetCounts = async () => {
      try {
        const response = await fetch(`${BASE_URL}/credits_usage`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        // Sum up counts across all users
        let videoCount = 0;
        let pptCount = 0;
        let posterCount = 0;
        let audioCount = 0;

        if (data.users && Array.isArray(data.users)) {
          data.users.forEach((user) => {
            if (user.file_generations && Array.isArray(user.file_generations)) {
              user.file_generations.forEach((gen) => {
                switch (gen.file_type) {
                  case "video":
                    videoCount += gen.count || 0;
                    break;
                  case "ppt":
                    pptCount += gen.count || 0;
                    break;
                  case "poster":
                    posterCount += gen.count || 0;
                    break;
                  case "audio":
                    audioCount += gen.count || 0;
                    break;
                  default:
                    break;
                }
              });
            }
          });
        }

        setAbstractVideosCount(videoCount);
        setPresentationsCount(pptCount);
        setConferencePosterCount(posterCount);
        setAudioSummariesCount(audioCount);
      } catch (error) {
        console.error("Error fetching asset counts:", error);
      }
    };

    const fetchPublications = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/getPublicationsWithFilesAndTasks`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();

        let publicationData = [];
        if (Array.isArray(data)) {
          publicationData = data;
        } else if (data && typeof data === "object") {
          publicationData = [data];
        }

        publicationData.sort((a, b) => {
          return b.id - a.id;
        });
        setPublications(publicationData);

        let rctCount = 0; // randomized clinical trial
        let ctCount = 0;  // clinical trial protocol
        let srCount = 0;  // systematic review
        let revCount = 0; // review

        publicationData.forEach((pub) => {
          const pubType = pub.type?.toLowerCase() || "";

          if (pubType === "randomized clinical trial") {
            rctCount++;
          } else if (pubType === "clinical trial protocol") {
            ctCount++;
          } else if (pubType === "systematic review & meta analysis") {
            srCount++;
          } else if (pubType === "review") {
            revCount++;
          }
        });

        setRandomClinicalTrialCount(rctCount);
        setClinicalTrialCount(ctCount);
        setSystematicReviewCount(srCount);
        setReviewCount(revCount);
      } catch (error) {
        console.error("Error fetching publications:", error);
      }
    };

    fetchAssetCounts();
    fetchPublications();
  }, []);

  // Fetch user analytics for monthly breakdown
  useEffect(() => {
    const fetchUserAnalytics = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      setIsLoadingAnalytics(true);
      try {
        const response = await fetch(
          `${BASE_URL}/user_analytics?period=month&month=${selectedMonth}&year=${selectedYear}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();

        if (data.weekly_breakdown && Array.isArray(data.weekly_breakdown)) {
          const formattedData = data.weekly_breakdown.map((week) => ({
            name: `Week ${week.week}`,
            Videos: week.videos_generated?.count || 0,
            PPTs: week.ppts_generated?.count || 0,
            Posters: week.posters_generated?.count || 0,
            Audios: week.audio_generated?.count || 0,
            Images: week.graphs_generated?.count || 0,
          }));
          setWeeklyData(formattedData);
        }
      } catch (error) {
        console.error("Error fetching user analytics:", error);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    fetchUserAnalytics();
  }, [selectedMonth, selectedYear, BASE_URL]);

  const handleNavigateToGenerationsHistory = (filter = null) => {
    if (filter) {
      navigate(`/GenerationsHistory?filter=${filter}`);
    } else {
      navigate("/GenerationsHistory");
    }
  };



  const totalPublications =
    randomClinicalTrialCount +
    clinicalTrialCount +
    systematicReviewCount +
    reviewCount;

  const chartData = [
    { label: "Total", value: totalPublications },
    { label: "Randomised Clinical Trials", value: randomClinicalTrialCount },
    { label: "Clinical Trials", value: clinicalTrialCount },
    { label: "Systematic Review & Meta Analysis", value: systematicReviewCount },
    { label: "Review", value: reviewCount },
  ];

  const maxVal = Math.max(...chartData.map((d) => d.value), 1);

  return (
    <SidebarTopbar>
      <div className="dashboard-container">
        <div className="top-row-cards">

          <div className="top-card">
            <div className="top-card-header-row">
              <HiOutlineDocumentChartBar className="card-icon" />
              <h4>PubGen</h4>
              <button
                className="btn-try"
                onClick={() => goToUpload("publicationView")}
              >
                Try now
              </button>
            </div>
            <p className="top-card-description">
              Generate Videos, PPTs, Posters, Social Media assets from your publications in minutes
            </p>
          </div>
          <div className="top-card">
            <div className="top-card-header-row">
              <HiOutlineDocumentMagnifyingGlass className="card-icon" />
              <h4>PubChat</h4>
              <button
                className="btn-try"
                onClick={() => navigate("/pubchat-upload")}
              >
                Try now
              </button>
            </div>
            <p className="top-card-description">
              Get concise, citation-linked answers, summaries, and follow-ups from your publications instantly
            </p>
          </div>


          <div className="top-card">
            <div className="top-card-header-row">
              <HiOutlineClipboardDocument className="card-icon" />
              <h4>PubLibrary</h4>
              <button
                className="btn-try"
                onClick={() => navigate("/pubLibrary")}
              >
                Try now
              </button>
            </div>
            <p className="top-card-description">
              Access to unlimited medical publications from various journals based on your therapy area instantly
            </p>
          </div>


        </div>

        <div className="generated-assets-container">
          <div className="generated-assets-header">
            <div className="title-and-icon">
              {/* <RiAiGenerate className="section-icon" /> */}
              <div>
                <h3>Generated Assets</h3>
                <p className="bottom-subtext">
                  A quick summary of all your generated assets
                </p>
              </div>
            </div>
          </div>

          <div className="assets-row">
            <div className="card asset-card">
              <div className="asset-card-header">
                <h4 className="asset-card-title">Videos</h4>
                <button
                  className="btn-view-asset"
                  onClick={() => handleNavigateToGenerationsHistory("abstractVideos")}
                >
                  View all
                </button>
              </div>
              <div className="asset-card-body">
                <div className="side-by-side">
                  <GrDocumentVideo className="asset-icon" />
                  <span className="count">{abstractVideosCount}</span>
                </div>
              </div>
            </div>

            <div className="card asset-card">
              <div className="asset-card-header">
                <h4 className="asset-card-title">Presentations</h4>
                <button
                  className="btn-view-asset"
                  onClick={() => handleNavigateToGenerationsHistory("presentations")}
                >
                  View all
                </button>
              </div>
              <div className="asset-card-body">
                <div className="side-by-side">
                  <GrDocumentPpt className="asset-icon" />
                  <span className="count">{presentationsCount}</span>
                </div>
              </div>
            </div>


            <div className="card asset-card">
              <div className="asset-card-header">
                <h4 className="asset-card-title">Posters</h4>
                <button
                  className="btn-view-asset"
                  onClick={() => handleNavigateToGenerationsHistory("posters")}
                >
                  View all
                </button>
              </div>
              <div className="asset-card-body">
                <div className="side-by-side">
                  <GrDocumentImage className="asset-icon" />
                  <span className="count">{conferencePosterCount}</span>
                </div>
              </div>
            </div>


            <div className="card asset-card">
              <div className="asset-card-header">
                <h4 className="asset-card-title">Audio Summaries</h4>
                <button
                  className="btn-view-asset"
                  onClick={() => handleNavigateToGenerationsHistory("audioSummaries")}
                >
                  View all
                </button>
              </div>
              <div className="asset-card-body">
                <div className="side-by-side">
                  <GrDocumentSound className="asset-icon" />
                  <span className="count">{audioSummariesCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Content Generation Chart */}
        <div className="monthly-analytics-section">
          <div className="card monthly-chart-card">
            <div className="monthly-chart-header">
              <div className="icon-title">
                {/* <VscGraph className="section-icon" /> */}
                <div>
                  <h3>Monthly Content Generation by Week</h3>
                  <p className="bottom-subtext">
                    Weekly breakdown of generated content across all formats
                  </p>
                </div>
              </div>
              <div className="month-selector">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="month-dropdown"
                >
                  {getAvailableMonths().map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="monthly-chart-container">
              {isLoadingAnalytics ? (
                <div className="chart-loading">Loading analytics...</div>
              ) : weeklyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={weeklyData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: '#666', fontSize: 12 }}
                      axisLine={{ stroke: '#e0e0e0' }}
                    />
                    <YAxis
                      tick={{ fill: '#666', fontSize: 12 }}
                      axisLine={{ stroke: '#e0e0e0' }}
                      label={{ value: 'Count (Assets Generated)', angle: -90, position: 'insideLeft', fill: '#666', style: { textAnchor: 'middle' } }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      cursor={{ fill: 'rgba(30, 77, 227, 0.05)' }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="rect"
                      iconSize={12}
                      layout="horizontal"
                      align="center"
                      verticalAlign="bottom"
                      itemStyle={{ marginRight: '20px' }}
                    />
                    <Bar dataKey="Audios" fill="#7b98f2" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Images" fill="#9ab1f7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="PPTs" fill="#3d66e8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Posters" fill="#5c7fed" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Videos" fill="#1e4de3" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-no-data">No data available for this month</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SidebarTopbar>
  );
};

export default Dashboard;